import {
  listRosterEventsPage,
  listTeamEventsPage,
  type ListTeamEventsQuery,
} from '@sportninja/common/services/event'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import { EVENTS_PER_PAGE_STREAM } from '../constants'
import type { ScheduleOrder } from '../filterEvents'
import { useEventStream, type EventStream } from './useEventStream'

export interface UseScheduleEventStreamArgs {
  teamId?: string
  /**
   * The team's roster for the selected season/division, read from
   * `/teams/{id}/schedules/V2`'s `team_roster_id`. Absent means no season is
   * selected — "All Seasons" — which switches to the team-wide endpoint.
   */
  rosterId?: string
  /**
   * Whether a season/division is selected at all. Distinguishes "All Seasons"
   * from "a season is selected but its roster has not resolved yet", which must
   * stay idle rather than silently listing every season.
   */
  hasScheduleSelection?: boolean
  /** True while the schedule filters are still loading. */
  isResolvingRoster?: boolean
  /** The selected period's window — the same one the games request sends. */
  startsAfter?: string | null
  startsBefore?: string | null
  order?: ScheduleOrder
}

/**
 * A TEAM's events, paginated, for the merged schedule list.
 *
 * The stream counterpart of `useScheduleEvents`, which drains every page. Same
 * two endpoints, same roster-vs-all-seasons split.
 *
 * ## What changed, and why it had to
 *
 * The fetch-all hook sends NO query: these endpoints accept params, but a
 * complete list could be windowed and sorted after it arrived, so it never
 * needed to. A paged stream cannot — you can only sort what you have already
 * pulled, and the merge's boundary is meaningless unless each page continues
 * where the last one stopped. So the window and sort go to the SERVER here,
 * and the client-side `filterEvents` pass downstream becomes a belt rather
 * than the only thing bounding the list.
 *
 * That has a knock-on the callers must handle: a windowed list can no longer
 * answer "does this team have anything today or later", which is the question
 * that picks the opening tab. Callers need a separate decision query for it —
 * see `useEventExistsFromToday`.
 */
export const useScheduleEventStream = ({
  teamId,
  rosterId,
  hasScheduleSelection = false,
  isResolvingRoster = false,
  startsAfter,
  startsBefore,
  order = 'asc',
}: UseScheduleEventStreamArgs): EventStream => {
  'use memo'

  const isAllSeasons = Boolean(teamId) && !hasScheduleSelection
  const isRosterScoped = Boolean(teamId) && hasScheduleSelection

  // Only set when given: an absent key means "no bound", which is not the same
  // as sending an empty one. PAST is open-ended at the start and legitimately
  // has no `starts_after`.
  const query: ListTeamEventsQuery = {
    per_page: EVENTS_PER_PAGE_STREAM,
    order,
    ...(startsAfter ? { starts_after: startsAfter } : {}),
    ...(startsBefore ? { starts_before: startsBefore } : {}),
  }

  const rosterStream = useEventStream({
    queryKey: queryKeys.events.byRosterStream(teamId, rosterId, query),
    fetchPage: (page) =>
      listRosterEventsPage(teamId as string, rosterId as string, query, page),
    // A season selected with no roster resolved stays idle: falling back to the
    // team-wide list would show other seasons' events under a season filter.
    enabled: isRosterScoped && Boolean(rosterId),
    order,
  })

  const teamStream = useEventStream({
    queryKey: queryKeys.events.byTeamStream(teamId, query),
    fetchPage: (page) => listTeamEventsPage(teamId as string, query, page),
    enabled: isAllSeasons,
    order,
  })

  const active = isAllSeasons ? teamStream : rosterStream

  /*
   * The roster has settled to "none": the filters are in, and no level of the
   * selection carries one. There is nothing left to wait for.
   *
   * `useEventStream` reports a DISABLED query as `isLoading: true`, which is
   * right while a gate is still resolving but wrong once it has resolved to
   * "nothing to fetch" -- the list would wait forever on a query that will
   * never run. Placeholder teams hit this on every visit, having no roster in
   * any division.
   */
  const rosterSettledToNone = isRosterScoped && !isResolvingRoster && !rosterId

  return {
    ...active,
    // A season whose roster has settled to "none" is done, not loading —
    // otherwise the list waits forever on a query that will never run.
    isLoading: rosterSettledToNone
      ? false
      : (isRosterScoped && isResolvingRoster) || active.isLoading,
  }
}
